import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ArrowLeft, ArrowRight, IdentificationCard } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useMyProfessional, useProfessionalMutations } from '@/hooks/useProfessionalsApi'

type ProfessionalProfileStepProps = {
  onNext: () => void
  onBack: () => void
  onSkip: () => void
  onRefresh: () => Promise<void>
}

export function ProfessionalProfileStep({ onNext, onBack, onSkip, onRefresh }: ProfessionalProfileStepProps) {
  const { t } = useTranslation('onboarding')
  const { user } = useAuth()
  const myProfessional = useMyProfessional()
  const mutations = useProfessionalMutations()

  const [displayName, setDisplayName] = useState('')
  const [specialty, setSpecialty] = useState('')
  const [bio, setBio] = useState('')
  const [prefilled, setPrefilled] = useState(false)

  useEffect(() => {
    if (prefilled) return
    const profile = myProfessional.data?.professional?.profile
    if (profile) {
      setDisplayName(profile.displayName ?? '')
      setSpecialty(profile.specialty ?? '')
      setBio(profile.bio ?? '')
      setPrefilled(true)
    } else if (!myProfessional.isLoading && user?.name) {
      setDisplayName((current) => current || user.name)
      setPrefilled(true)
    }
  }, [myProfessional.data, myProfessional.isLoading, prefilled, user?.name])

  const handleSave = async () => {
    if (!user) return
    if (!displayName.trim()) {
      toast.error(t('professional.profile.validation.nameRequired'))
      return
    }

    try {
      await mutations.upsertProfile(user.id, {
        displayName: displayName.trim(),
        specialty: specialty.trim() || null,
        bio: bio.trim() || null,
      })
      toast.success(t('professional.profile.saved'))
      await onRefresh()
      onNext()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : t('professional.profile.error'))
    }
  }

  return (
    <div className="flex flex-col px-4 py-6 animate-in fade-in duration-500">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F08A24] to-[#f9a84c] mb-4">
          <IdentificationCard weight="bold" className="w-7 h-7 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-[#0B2A4A] dark:text-white mb-2">
          {t('professional.profile.title')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          {t('professional.profile.subtitle')}
        </p>
      </div>

      <div className="max-w-lg mx-auto w-full">
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="professional-display-name">
                {t('professional.profile.form.displayName')}
              </Label>
              <Input
                id="professional-display-name"
                placeholder={t('professional.profile.form.displayNamePlaceholder')}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="professional-specialty">
                {t('professional.profile.form.specialty')}
              </Label>
              <Input
                id="professional-specialty"
                placeholder={t('professional.profile.form.specialtyPlaceholder')}
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="professional-bio">{t('professional.profile.form.bio')}</Label>
              <Textarea
                id="professional-bio"
                rows={4}
                placeholder={t('professional.profile.form.bioPlaceholder')}
                value={bio}
                onChange={(e) => setBio(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center justify-between mt-10 max-w-lg mx-auto w-full">
        <Button
          onClick={onBack}
          variant="ghost"
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
        >
          <ArrowLeft weight="bold" className="mr-2 w-4 h-4" />
          {t('back')}
        </Button>

        <div className="flex items-center gap-3">
          <Button
            onClick={onSkip}
            variant="ghost"
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500"
          >
            {t('skipStep')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={mutations.isLoading || myProfessional.isLoading}
            className="bg-[#F08A24] hover:bg-[#e07a14] text-white"
          >
            {mutations.isLoading ? t('professional.profile.saving') : t('continue')}
            <ArrowRight weight="bold" className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
